import { useState, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useFgp } from "@/context/FgpContext";
import { aiSuggestTasks, aiTranscribe } from "@/lib/ai";
import { Mic, Square, Check, Sparkles, RotateCcw, Type, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export const VoiceNoteDrawer = ({ open, onOpenChange }) => {
  const { addTask, addNote } = useFgp();
  const [tab, setTab] = useState("record");

  // recording state
  const [stage, setStage] = useState("idle"); // idle | recording | transcribing | review
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // typed note state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [typedTasks, setTypedTasks] = useState([]);
  const [taskDraft, setTaskDraft] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    streamRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        cleanupStream();
        setStage("idle"); setSeconds(0); setTab("record");
        setTitle(""); setBody(""); setTypedTasks([]); setTaskDraft("");
        setTranscript(""); setSuggestions([]); setSelected([]);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (stage !== "recording") return;
    const i = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(i);
  }, [stage]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => processRecording();
      mediaRef.current = mr;
      mr.start();
      setSeconds(0); setStage("recording");
    } catch {
      toast.error("Microphone access denied. Enable it or use the Type tab.");
    }
  };

  const stopRec = () => {
    try { mediaRef.current?.stop(); } catch { /* */ }
    setStage("transcribing");
  };

  const processRecording = async () => {
    cleanupStream();
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    try {
      const text = await aiTranscribe(blob);
      if (!text) { toast.error("Nothing was captured — try again."); setStage("idle"); return; }
      // Show the transcript immediately, then draft tasks in the background.
      setTranscript(text);
      setSuggestions([]); setSelected([]);
      setStage("review");
      setSuggLoading(true);
      try {
        const tasks = await aiSuggestTasks(text);
        setSuggestions(tasks);
        setSelected(tasks.map(() => true));
      } catch {
        toast.info("Couldn't draft tasks automatically — you can add them manually.");
      } finally {
        setSuggLoading(false);
      }
    } catch (err) {
      toast.error(err.message || "Could not process recording");
      setStage("idle");
    }
  };

  const saveVoice = () => {
    const chosen = suggestions.filter((_, i) => selected[i]);
    chosen.forEach((t) => addTask(t.title));
    addNote({ title: "Voice note", type: "voice", duration: fmt(seconds || 0), transcript, suggestedTasks: suggestions.map((t) => t.title) });
    toast.success(`Voice note saved · ${chosen.length} task${chosen.length !== 1 ? "s" : ""} created`);
    onOpenChange(false);
  };

  const suggestFromTyped = async () => {
    if (!body.trim()) return toast.error("Write something first");
    setSuggesting(true);
    try {
      const tasks = await aiSuggestTasks(body);
      if (!tasks.length) return toast.info("No clear tasks found in that note");
      setTypedTasks((prev) => [...prev, ...tasks.map((t) => t.title)]);
      toast.success(`${tasks.length} task${tasks.length !== 1 ? "s" : ""} suggested`);
    } catch (err) {
      toast.error(err.message || "AI is unavailable right now");
    } finally {
      setSuggesting(false);
    }
  };

  const saveTyped = () => {
    if (!body.trim()) return toast.error("Write something first");
    typedTasks.forEach((t) => addTask(t));
    addNote({ title: title.trim() || "Typed note", type: "text", duration: "—", transcript: body, suggestedTasks: typedTasks });
    toast.success(`Note saved${typedTasks.length ? ` · ${typedTasks.length} task(s) created` : ""}`);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[450px] sm:max-w-[450px] p-0 flex flex-col" data-testid="voice-note-drawer">
        <SheetHeader className="px-6 py-5 border-b border-slate-100">
          <SheetTitle className="font-heading flex items-center gap-2"><Sparkles className="h-4 w-4 text-neutral-600" /> Capture a note</SheetTitle>
          <SheetDescription>Record or type — Swishr's AI drafts suggested tasks for your review.</SheetDescription>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-4">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="record" data-testid="note-tab-record" className="gap-1.5"><Mic className="h-4 w-4" /> Record</TabsTrigger>
              <TabsTrigger value="type" data-testid="note-tab-type" className="gap-1.5"><Type className="h-4 w-4" /> Type</TabsTrigger>
            </TabsList>
          </div>

          {/* RECORD */}
          <TabsContent value="record" className="flex-1 overflow-y-auto px-6 py-6 mt-0">
            <AnimatePresence mode="wait">
              {(stage === "idle" || stage === "recording") && (
                <motion.div key="rec" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-10">
                  <button onClick={stage === "recording" ? stopRec : startRec} data-testid="voice-record-toggle"
                    className={`relative h-24 w-24 rounded-full flex items-center justify-center text-white transition-colors ${stage === "recording" ? "bg-rose-500 pulse-ring" : "bg-neutral-600 hover:bg-neutral-700"}`}>
                    {stage === "recording" ? <Square className="h-8 w-8 fill-white" /> : <Mic className="h-9 w-9" />}
                  </button>
                  <p className="mt-6 font-heading text-2xl font-semibold tabular-nums text-slate-900">{fmt(seconds)}</p>
                  <p className="mt-1 text-sm text-slate-500">{stage === "recording" ? "Listening… tap to stop" : "Tap the mic to start recording"}</p>
                </motion.div>
              )}
              {stage === "transcribing" && (
                <motion.div key="trans" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-12 w-12 rounded-full border-2 border-neutral-200 border-t-neutral-600 animate-spin" />
                  <p className="mt-5 font-medium text-slate-700">Transcribing your note…</p>
                  <p className="text-sm text-slate-400">Usually just a few seconds</p>
                </motion.div>
              )}
              {stage === "review" && (
                <motion.div key="review" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Transcript</p>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 leading-relaxed" data-testid="voice-transcript">{transcript}</div>
                  </div>
                  <div className="rounded-xl border border-neutral-200 bg-gradient-to-br from-white to-neutral-50/40 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="h-4 w-4 text-neutral-600" /><p className="text-sm font-semibold text-slate-800">Suggested tasks</p>
                      <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-neutral-600 bg-neutral-100 px-2 py-0.5 rounded-full">AI draft</span>
                    </div>
                    {suggLoading ? (
                      <div className="flex items-center gap-2 text-sm text-slate-400 py-2" data-testid="suggestions-loading"><Loader2 className="h-4 w-4 animate-spin" /> Drafting suggested tasks…</div>
                    ) : suggestions.length === 0 ? (
                      <p className="text-sm text-slate-400">No clear tasks found — you can still save the note.</p>
                    ) : (
                      <div className="space-y-2">
                        {suggestions.map((t, i) => (
                          <label key={i} data-testid={`suggested-task-${i}`} className="flex items-start gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2.5 cursor-pointer hover:border-neutral-300 transition-colors">
                            <input type="checkbox" checked={selected[i]} onChange={() => setSelected((s) => s.map((v, idx) => idx === i ? !v : v))} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-neutral-600 focus:ring-neutral-500" />
                            <span className="text-sm text-slate-700">{t.title}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="mt-3 text-xs text-slate-400">Review before saving — nothing is added to records automatically.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => { setStage("idle"); setSeconds(0); setTranscript(""); setSuggestions([]); }} data-testid="voice-redo" className="gap-2"><RotateCcw className="h-4 w-4" /> Re-record</Button>
                    <Button onClick={saveVoice} data-testid="voice-save" className="flex-1 gap-2 bg-neutral-600 hover:bg-neutral-700"><Check className="h-4 w-4" /> Save note & tasks</Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </TabsContent>

          {/* TYPE */}
          <TabsContent value="type" className="flex-1 overflow-y-auto px-6 py-6 mt-0 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. After call with the school" data-testid="note-title-input" className="mt-1.5" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Note</label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Type your note here…" data-testid="note-body-input" className="mt-1.5" />
              <Button variant="outline" size="sm" onClick={suggestFromTyped} disabled={suggesting} data-testid="note-ai-suggest" className="mt-2 gap-1.5">
                {suggesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-neutral-600" />} Suggest tasks with AI
              </Button>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Tasks to create (optional)</label>
              <div className="flex gap-2 mt-1.5">
                <Input value={taskDraft} onChange={(e) => setTaskDraft(e.target.value)} placeholder="Add a task…" data-testid="note-task-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && taskDraft.trim()) { setTypedTasks([...typedTasks, taskDraft.trim()]); setTaskDraft(""); } }} />
                <Button variant="outline" onClick={() => { if (taskDraft.trim()) { setTypedTasks([...typedTasks, taskDraft.trim()]); setTaskDraft(""); } }} data-testid="note-add-task-btn" className="gap-1"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="mt-2 space-y-1.5">
                {typedTasks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-neutral-50 border border-neutral-100 px-3 py-2 text-sm text-slate-700">
                    <Check className="h-3.5 w-3.5 text-neutral-600" /><span className="flex-1">{t}</span>
                    <button onClick={() => setTypedTasks(typedTasks.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={saveTyped} data-testid="note-save-typed" className="w-full gap-2 bg-neutral-600 hover:bg-neutral-700"><Check className="h-4 w-4" /> Save note</Button>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
